import { Routes } from '@angular/router';
import { AuthGuard } from '../guards/auth.guard';
import { GuestGuard } from '../guards/guest.guard';
import { AuthFlowGuard } from '../guards/auth-flow.guard';
import { DevOnlyGuard } from '../guards/dev-only.guard';
import { SignupFlowGuard } from '../guards/signup-flow.guard';
import { EmailTemplateNavigationGuard } from '../guards/email-template-navigation.guard';
import { AppointmentTypeFormsNavigationGuard } from '../guards/appointment-type-forms-navigation.guard';

export const routes: Routes = [
  // Dev-auth route - protected by DevOnlyGuard which checks runtime config
  {
    path: 'dev-auth',
    loadComponent: () => import('../pages/dev-auth/dev-auth.page').then(m => m.DevAuthPage),
    canActivate: [DevOnlyGuard]
  },
  // Runtime-only i18n certification surface. The route uses the real portal
  // bootstrap and profile localization services, but remains unavailable
  // outside lower environments and requires an authenticated session.
  {
    path: '__test/i18n-certification',
    loadComponent: () => import('../pages/i18n-certification/i18n-certification.page').then(m => m.I18nCertificationPage),
    canActivate: [DevOnlyGuard, AuthGuard]
  },
  {
    path: '',
    loadComponent: () => import('../components/auth-redirect/auth-redirect.component').then(m => m.AuthRedirectComponent),
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () => import('../pages/login/login.page').then( m => m.LoginPage),
    canActivate: [GuestGuard]
  },
  {
    path: 'auth/impersonate',
    loadComponent: () => import('../pages/impersonate/impersonate.page').then(m => m.ImpersonatePage)
  },
  {
    path: 'verify',
    loadComponent: () => import('../pages/verify/verify.page').then( m => m.VerifyPage),
    canActivate: [AuthFlowGuard],
    data: { requiredStep: 'verify' }
  },
  {
    path: 'device-verify',
    loadComponent: () => import('../pages/device-verify/device-verify.page').then( m => m.DeviceVerifyPage),
    canActivate: [AuthFlowGuard],
    data: { requiredStep: 'device-verify' }
  },
  {
    path: 'factor-setup',
    loadComponent: () => import('../pages/factor-setup/factor-setup.page').then( m => m.FactorSetupPage),
    canActivate: [AuthFlowGuard],
    data: { requiredStep: 'factor-setup' }
  },
  // Signup flow routes
  {
    path: 'signup',
    loadComponent: () => import('../pages/signup/signup-email/signup-email.page').then(m => m.SignupEmailPage),
    canActivate: [SignupFlowGuard],
    data: { requiredStep: 'email' }
  },
  {
    path: 'signup/email',
    redirectTo: 'signup',
    pathMatch: 'full'
  },
  {
    path: 'signup/verify',
    loadComponent: () => import('../pages/signup/signup-verify/signup-verify.page').then(m => m.SignupVerifyPage),
    canActivate: [SignupFlowGuard],
    data: { requiredStep: 'verify' }
  },
  {
    path: 'signup/country',
    loadComponent: () => import('../pages/signup/signup-country/signup-country.page').then(m => m.SignupCountryPage),
    canActivate: [SignupFlowGuard],
    data: { requiredStep: 'country' }
  },
  {
    path: 'signup/plan',
    loadComponent: () => import('../pages/signup/signup-plan/signup-plan.page').then(m => m.SignupPlanPage),
    canActivate: [SignupFlowGuard],
    data: { requiredStep: 'plan' }
  },
  {
    path: 'signup/business',
    loadComponent: () => import('../pages/signup/signup-business/signup-business.page').then(m => m.SignupBusinessPage),
    canActivate: [SignupFlowGuard],
    data: { requiredStep: 'business' }
  },
  {
    path: 'signup/payment',
    loadComponent: () => import('../pages/signup/signup-payment/signup-payment.page').then(m => m.SignupPaymentPage),
    canActivate: [SignupFlowGuard],
    data: { requiredStep: 'payment' }
  },
  {
    path: 'signup/welcome',
    loadComponent: () => import('../pages/signup/signup-welcome/signup-welcome.page').then(m => m.SignupWelcomePage),
    canActivate: [SignupFlowGuard],
    data: { requiredStep: 'welcome' }
  },
  {
    path: 'chat',
    loadComponent: () => import('../pages/chat/chat-launcher.page').then(m => m.ChatLauncherPage),
    canActivate: [AuthGuard]
  },
  {
    path: 'home',
    loadComponent: () => import('../pages/home/home.page').then(m => m.HomePage),
    canActivate: [AuthGuard]
  },
  {
    path: 'requests',
    loadComponent: () => import('../pages/requests/requests.page').then(m => m.RequestsPage),
    canActivate: [AuthGuard]
  },
  {
    path: 'requests/:requestId',
    loadComponent: () => import('../pages/requests/request-detail.page').then(m => m.RequestDetailPage),
    canActivate: [AuthGuard]
  },
  {
    path: 'apps',
    loadComponent: () => import('../pages/apps/apps.page').then( m => m.AppsPage),
    canActivate: [AuthGuard]
  },
  // Multi-service booking (ADO 16797) — dedicated page, registered before the
  // dynamic-page catchall so both URL forms resolve to the custom component.
  {
    path: 'apps/appointments/pages/multi-service-booking',
    loadComponent: () => import('./components/appointments/multi-service-booking/multi-service-booking.page').then(m => m.MultiServiceBookingPage),
    canActivate: [AuthGuard]
  },
  // Walk-in booking (ADO 17132) must resolve to the dedicated component
  // before the generic apps/:appId/pages/:pageId dynamic page route.
  {
    path: 'apps/appointments/pages/walk-in-booking',
    loadComponent: () => import('./components/appointments/walk-in-booking/walk-in-booking.component').then(m => m.WalkInBookingComponent),
    canActivate: [AuthGuard]
  },
  // Reassign staff (ADO 17251) — dedicated full-page workflow that must
  // resolve before the generic apps/:appId/pages/:pageId dynamic page route.
  {
    path: 'apps/appointments/pages/appointment-reassign-staff',
    loadComponent: () => import('../components/appointments/appointment-reassign-staff/appointment-reassign-staff.component').then(m => m.AppointmentReassignStaffComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'apps/:appId/pages/:pageId',
    loadComponent: () => import('../pages/beta-dynamic-page/beta-dynamic-page.page').then( m => m.BetaDynamicPagePage),
    canActivate: [AuthGuard, EmailTemplateNavigationGuard, AppointmentTypeFormsNavigationGuard],
    canDeactivate: [EmailTemplateNavigationGuard, AppointmentTypeFormsNavigationGuard],
    runGuardsAndResolvers: 'always'
  },
  {
    path: 'apps/:appId/actions',
    loadComponent: () => import('../components/actions/actions-directory/actions-directory.component').then( m => m.ActionsDirectoryComponent),
    canActivate: [AuthGuard, EmailTemplateNavigationGuard],
    canDeactivate: [EmailTemplateNavigationGuard]
  },
  {
    path: 'apps/:appId/actions/:actionCode',
    loadComponent: () => import('../pages/beta-dynamic-page/beta-dynamic-page.page').then( m => m.BetaDynamicPagePage),
    canActivate: [AuthGuard, EmailTemplateNavigationGuard],
    canDeactivate: [EmailTemplateNavigationGuard],
    runGuardsAndResolvers: 'always'
  },
  {
    path: 'tasks/list',
    loadComponent: () => import('../pages/tasks/task-list-detail.page').then(m => m.TaskListDetailPage),
    canActivate: [AuthGuard]
  },
  {
    path: 'tasks/list/:taskListId',
    loadComponent: () => import('../pages/tasks/task-list-detail.page').then(m => m.TaskListDetailPage),
    canActivate: [AuthGuard]
  },
  // SaaS billing prototype stub (#01 / plans/saas-billing). Lazy-loaded.
  // Anchor URL referenced by docs #02-#10; full UI lives in #03 onward.
  {
    path: 'billing',
    loadComponent: () => import('../pages/billing/billing.page').then(m => m.BillingPage)
  },
  // #08: AI credits detail page. Listed BEFORE /billing/credits/top-up so the
  // more specific top-up route still matches first via Angular's prefix logic
  // (most specific path under '' wins via order, but Angular evaluates in
  // declaration order so top-up must come FIRST when using shared prefixes).
  {
    path: 'billing/credits/top-up',
    loadComponent: () =>
      import('../pages/billing/billing-credits-top-up.page').then(
        m => m.BillingCreditsTopUpPage
      ),
    canActivate: [AuthGuard]
  },
  {
    path: 'billing/credits',
    loadComponent: () =>
      import('../pages/billing/billing-credits.page').then(m => m.BillingCreditsPage),
    canActivate: [AuthGuard]
  },
  // SPIKE: Stripe Customer Portal sandbox discovery. /billingportal lets the
  // user click through Stripe's hosted experience side-by-side with /billing.
  // No AuthGuard — spike must be hittable cold. Delete this entry, the page
  // component, and the matching backend controller when the spike concludes.
  // See plans/saas-billing/spike-stripe-portal-comparison.md.
  {
    path: 'billingportal',
    loadComponent: () =>
      import('../pages/billing-portal-spike/billing-portal-spike.page').then(
        m => m.BillingPortalSpikePage
      )
  },
  {
    path: 'not-found',
    loadComponent: () => import('../pages/not-found/not-found.page').then(m => m.NotFoundPage)
  },
  // Multi-service booking shorthand URL (see dedicated route above)
  {
    path: 'appointments/multi-service-booking',
    loadComponent: () => import('./components/appointments/multi-service-booking/multi-service-booking.page').then(m => m.MultiServiceBookingPage),
    canActivate: [AuthGuard]
  },
  // Catch-all for shorthand app/page URLs (e.g., /reports/board)
  {
    path: ':appId/:pageId',
    loadComponent: () => import('../pages/beta-dynamic-page/beta-dynamic-page.page').then(m => m.BetaDynamicPagePage),
    canActivate: [AuthGuard, EmailTemplateNavigationGuard, AppointmentTypeFormsNavigationGuard],
    canDeactivate: [EmailTemplateNavigationGuard, AppointmentTypeFormsNavigationGuard],
    runGuardsAndResolvers: 'always'
  },
  {
    path: '**',
    redirectTo: 'not-found'
  },
];
